import type { ReactNode } from 'react';
import { Disclosure } from '@/components/UI';
import { formatExitCandidate, formatHash, formatStrategyVersionKey } from './backtestStatusFormatters';
import type { StrategyCoverage, StrategyQueueStatus, StrategyScorecardContext } from './backtestStatusTypes';

interface BacktestDiagnosticsProps {
    coverage: StrategyCoverage;
    queueStatus: StrategyQueueStatus | undefined;
    scorecardContext: StrategyScorecardContext | undefined;
    className?: string;
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</div>
        <div className="space-y-0.5 text-xs text-zinc-300">{children}</div>
    </div>
);

const Row = ({ label, value, mono = true }: { label: string; value: ReactNode; mono?: boolean }) => (
    <div className="flex justify-between gap-3">
        <span className="text-zinc-500">{label}</span>
        <span className={`text-right ${mono ? 'font-mono' : ''} break-all`}>{value}</span>
    </div>
);

const BacktestDiagnostics = ({ coverage, queueStatus, scorecardContext, className = '' }: BacktestDiagnosticsProps) => {
    const versionSummary = coverage.backtestVersionKeySummary;
    const configuredExitCandidates = coverage.configuredExitCandidates ?? [];
    const configuredOptionIds = coverage.configuredOptionBuyingStrategyIds ?? [];
    const unpopulatedExitCandidates = coverage.unpopulatedExitCandidates ?? [];
    const unpopulatedOptionIds = coverage.unpopulatedOptionBuyingStrategyIds ?? [];

    return (
        <Disclosure className={className} title="Diagnostics (advanced)">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Section title="Version keys">
                    <Row label="Mode" value={coverage.strategyVersionKeyMode} />
                    <Row label="Current key" value={formatStrategyVersionKey(coverage.strategyVersionKey)} />
                    <Row
                        label="Backtests · current key"
                        value={`${versionSummary.currentKeyBacktestsCount}${versionSummary.currentKeyLastUpdateDateTime ? ` (${versionSummary.currentKeyLastUpdateDateTime})` : ''}`}
                    />
                    <Row label="Other keys" value={versionSummary.otherKeysCount} />
                    {versionSummary.otherKeys.slice(0, 5).map((row) => (
                        <Row key={row.strategyVersionKey} label={formatStrategyVersionKey(row.strategyVersionKey)} value={row.count} />
                    ))}
                    {versionSummary.otherKeysCount > 5 && (
                        <div className="text-zinc-600">Showing first 5 of {versionSummary.otherKeysCount} other keys.</div>
                    )}
                </Section>

                <Section title="Coverage internals">
                    <Row label="Entities in universe" value={coverage.totalEntitiesInUniverse} />
                    <Row label="Stored backtests" value={coverage.totalBacktests} />
                    <Row label="Population expected" value={coverage.populationProgress.expectedBacktests} />
                    <Row label="Population complete" value={coverage.populationProgress.completedBacktests} />
                    <Row label="Population active" value={coverage.populationProgress.activeBacktests} />
                    <Row label="Population failed" value={coverage.populationProgress.failedBacktests} />
                    <Row label="Population not queued" value={coverage.populationProgress.unqueuedBacktests} />
                    <Row
                        label="Effective exit candidates"
                        value={
                            coverage.effectiveExitCandidateAttachments.length
                                ? coverage.effectiveExitCandidateAttachments
                                      .map(
                                          (candidate) =>
                                              `#${candidate.candidateId}:${formatHash(candidate.candidateHash)}${candidate.isDefault === 1 ? ' (default)' : ''}`
                                      )
                                      .join(', ')
                                : 'None'
                        }
                    />
                    <Row
                        label="Backtested exit candidates"
                        value={
                            coverage.exitCandidates.length
                                ? coverage.exitCandidates.map((candidate) => formatExitCandidate(candidate)).join(', ')
                                : 'None'
                        }
                    />
                    <Row
                        label="Option strategy IDs"
                        value={coverage.optionBuyingStrategyIds.length ? coverage.optionBuyingStrategyIds.join(', ') : 'None'}
                    />
                    {configuredExitCandidates.length > 0 && (
                        <Row
                            label="Configured exit candidates"
                            value={configuredExitCandidates
                                .map((candidate) => `#${candidate.candidateId}:${formatHash(candidate.candidateHash)}`)
                                .join(', ')}
                        />
                    )}
                    {configuredOptionIds.length > 0 && <Row label="Configured option IDs" value={configuredOptionIds.join(', ')} />}
                    {unpopulatedExitCandidates.length > 0 && (
                        <Row
                            label="Unpopulated exit candidates"
                            value={unpopulatedExitCandidates
                                .map((candidate) => `#${candidate.candidateId}:${formatHash(candidate.candidateHash)}`)
                                .join(', ')}
                        />
                    )}
                    {unpopulatedOptionIds.length > 0 && <Row label="Unpopulated option IDs" value={unpopulatedOptionIds.join(', ')} />}
                    <Row label="Oldest update" value={coverage.oldestLastUpdateDateTime || 'N/A'} />
                    <Row label="Newest update" value={coverage.newestLastUpdateDateTime || 'N/A'} />
                </Section>

                {queueStatus && (
                    <Section title="Queue">
                        <Row label="Environment" mono={false} value={queueStatus.environment} />
                        <Row label="Mode" value={queueStatus.strategyVersionKeyMode} />
                        <Row label="Current key" value={formatStrategyVersionKey(queueStatus.strategyVersionKey)} />
                    </Section>
                )}

                {scorecardContext && (
                    <Section title="Scorecard">
                        {scorecardContext.hasAnyScorecard ? (
                            <>
                                {scorecardContext.activeScorecard && (
                                    <Row
                                        label="Attached scorecard"
                                        mono={false}
                                        value={`${scorecardContext.activeScorecard.name} (id ${scorecardContext.activeScorecard.id})`}
                                    />
                                )}
                            </>
                        ) : (
                            <div className="text-zinc-500">No scorecard configured for this strategy.</div>
                        )}
                    </Section>
                )}
            </div>
        </Disclosure>
    );
};

export default BacktestDiagnostics;
